import React, { useEffect, useMemo, useRef, useState } from 'react';
import { format, parseISO, isValid, startOfDay } from 'date-fns';
import {
  Calendar as CalendarIcon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Calendar } from '../../../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { cn } from '../../../lib/utils';

function toIsoDate(date) {
  if (!date || !isValid(date)) return '';
  return format(date, 'yyyy-MM-dd');
}

function fromIsoDate(value) {
  if (!value) return undefined;
  try {
    const parsed = parseISO(value);
    return isValid(parsed) ? startOfDay(parsed) : undefined;
  } catch {
    return undefined;
  }
}

function formatDisplayDate(value) {
  const date = fromIsoDate(value);
  if (!date) return '';
  return format(date, 'MMM d, yyyy');
}

export function formatDueRangeLabel(start, end) {
  if (!start && !end) return '';
  if (start && end) return `${formatDisplayDate(start)} – ${formatDisplayDate(end)}`;
  return formatDisplayDate(start || end);
}

const MONTH_NAMES = Array.from({ length: 12 }, (_, index) => (
  format(new Date(2020, index, 1), 'MMMM')
));

/**
 * Move a date to another month/year, preserving the day when it exists and
 * clamping to the final valid day otherwise (Jan 31 -> Feb 2026 = Feb 28).
 * Handles month-length differences and leap years via Date arithmetic.
 */
export function clampDayToMonth(date, monthIndex, year) {
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  return new Date(year, monthIndex, Math.min(date.getDate(), lastDay));
}

function CalendarDropdownField({
  label,
  display,
  open,
  onToggle,
  options,
  isSelected,
  onSelect,
  triggerClassName,
  listClassName,
  testId,
}) {
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Center the current selection once the list has rendered. Scroll the
    // list directly (instant) — scrollIntoView with CSS smooth scrolling can
    // be interrupted and also scrolls ancestor containers.
    requestAnimationFrame(() => {
      const list = listRef.current;
      const selected = list?.querySelector('[data-selected]');
      if (!list || !selected) return;
      list.scrollTo({
        top: selected.offsetTop - (list.clientHeight - selected.offsetHeight) / 2,
        behavior: 'instant',
      });
    });
  }, [open]);

  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        data-testid={testId}
        onClick={onToggle}
        className={cn(
          'flex h-8 cursor-pointer items-center justify-between gap-1.5 rounded-md border border-border bg-muted/40 px-2.5 text-sm font-medium text-foreground transition-colors',
          'hover:bg-muted/70',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary',
          triggerClassName,
        )}
      >
        <span className="truncate">{display}</span>
        {/* Decorative only — clicks pass through to the trigger button. */}
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none h-4 w-4 shrink-0 text-muted-foreground"
        />
      </button>
      {open ? (
        <div
          ref={listRef}
          role="listbox"
          aria-label={`${label} options`}
          data-testid={`${testId}-list`}
          className={cn(
            'absolute left-0 top-[calc(100%+6px)] z-[90] max-h-56 overflow-y-auto scroll-smooth rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-lg',
            '[overscroll-behavior:contain] [scrollbar-width:thin]',
            listClassName,
          )}
        >
          {options.map((option) => {
            const selected = isSelected(option.value);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={selected}
                data-selected={selected || undefined}
                className={cn(
                  'flex h-9 w-full cursor-pointer items-center rounded-lg px-2.5 text-sm font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary',
                  selected
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-foreground hover:bg-muted',
                )}
                onClick={() => onSelect(option.value)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function MonthYearControls({ month, onMonthChange }) {
  // 'month' | 'year' | null — a single slot guarantees only one open dropdown.
  const [openDropdown, setOpenDropdown] = useState(null);
  const containerRef = useRef(null);
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const years = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 21 }, (_, index) => current - 10 + index);
  }, []);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpenDropdown(null);
    };
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      // Capture-phase stop so Escape closes only the dropdown, not the
      // surrounding calendar popover/drawer.
      event.stopPropagation();
      setOpenDropdown(null);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [openDropdown]);

  return (
    <div ref={containerRef} className="flex items-center gap-2">
      <CalendarDropdownField
        label="Select month"
        display={MONTH_NAMES[monthIndex]}
        open={openDropdown === 'month'}
        onToggle={() => setOpenDropdown((current) => (current === 'month' ? null : 'month'))}
        options={MONTH_NAMES.map((name, index) => ({ value: index, label: name }))}
        isSelected={(value) => value === monthIndex}
        onSelect={(index) => {
          onMonthChange(clampDayToMonth(month, index, year));
          setOpenDropdown(null);
        }}
        triggerClassName="w-[8.5rem]"
        listClassName="w-40"
        testId="task-calendar-month"
      />
      <CalendarDropdownField
        label="Select year"
        display={String(year)}
        open={openDropdown === 'year'}
        onToggle={() => setOpenDropdown((current) => (current === 'year' ? null : 'year'))}
        options={years.map((entry) => ({ value: entry, label: String(entry) }))}
        isSelected={(value) => value === year}
        onSelect={(nextYear) => {
          onMonthChange(clampDayToMonth(month, monthIndex, nextYear));
          setOpenDropdown(null);
        }}
        triggerClassName="w-[5.5rem]"
        listClassName="w-28"
        testId="task-calendar-year"
      />
    </div>
  );
}

/**
 * Shared single/range calendar used by the Due Date filter and Task drawer.
 * Reuses the design-system Calendar (react-day-picker) — no standalone mock data.
 */
export function TaskDateCalendar({
  mode = 'single',
  open,
  onOpenChange,
  value,
  rangeValue,
  onConfirm,
  onClear,
  timezoneLabel,
  trigger,
  align = 'start',
  numberOfMonths,
}) {
  const isRange = mode === 'range';
  const months = numberOfMonths ?? (isRange ? 2 : 1);
  const [visibleMonth, setVisibleMonth] = useState(() => (
    fromIsoDate(isRange ? rangeValue?.start : value) || new Date()
  ));
  const [draftSingle, setDraftSingle] = useState(() => fromIsoDate(value));
  const [draftRange, setDraftRange] = useState(() => ({
    from: fromIsoDate(rangeValue?.start),
    to: fromIsoDate(rangeValue?.end),
  }));

  useEffect(() => {
    if (!open) return;
    setDraftSingle(fromIsoDate(value));
    setDraftRange({
      from: fromIsoDate(rangeValue?.start),
      to: fromIsoDate(rangeValue?.end),
    });
    setVisibleMonth(
      fromIsoDate(isRange ? rangeValue?.start : value) || new Date(),
    );
  }, [open, value, rangeValue?.start, rangeValue?.end, isRange]);

  const rangeComplete = Boolean(draftRange.from && draftRange.to);
  const summary = isRange
    ? (rangeComplete
      ? formatDueRangeLabel(toIsoDate(draftRange.from), toIsoDate(draftRange.to))
      : draftRange.from
        ? `Start ${formatDisplayDate(toIsoDate(draftRange.from))} — select end date`
        : 'Select a start and end date')
    : (draftSingle ? formatDisplayDate(toIsoDate(draftSingle)) : 'Select a date');

  const close = () => onOpenChange?.(false);

  const handleConfirm = () => {
    if (isRange) {
      if (!rangeComplete) return;
      onConfirm?.({
        start: toIsoDate(draftRange.from),
        end: toIsoDate(draftRange.to),
      });
    } else {
      onConfirm?.(toIsoDate(draftSingle) || '');
    }
    close();
  };

  const handleClear = () => {
    setDraftSingle(undefined);
    setDraftRange({ from: undefined, to: undefined });
    onClear?.();
  };

  const shiftMonth = (delta) => {
    setVisibleMonth(clampDayToMonth(
      visibleMonth,
      visibleMonth.getMonth() + delta,
      visibleMonth.getFullYear(),
    ));
  };

  const body = (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2.5 sm:px-4">
        <MonthYearControls month={visibleMonth} onMonthChange={setVisibleMonth} />
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            aria-label="Previous month"
            onClick={() => shiftMonth(-1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 px-2"
            onClick={() => {
              const today = startOfDay(new Date());
              setVisibleMonth(today);
              if (!isRange) setDraftSingle(today);
            }}
            data-testid="task-calendar-today"
          >
            Today
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            aria-label="Next month"
            onClick={() => shiftMonth(1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {timezoneLabel ? (
        <p className="border-b border-border px-3 py-1.5 text-xs text-muted-foreground sm:px-4">
          Workspace timezone: {timezoneLabel}
        </p>
      ) : null}

      <div className="overflow-x-auto p-2 sm:p-3">
        {isRange ? (
          <Calendar
            mode="range"
            numberOfMonths={months}
            month={visibleMonth}
            onMonthChange={setVisibleMonth}
            selected={draftRange}
            onSelect={(range) => {
              setDraftRange({
                from: range?.from,
                to: range?.to,
              });
            }}
            className="rounded-md"
          />
        ) : (
          <Calendar
            mode="single"
            numberOfMonths={1}
            month={visibleMonth}
            onMonthChange={setVisibleMonth}
            selected={draftSingle}
            onSelect={(date) => setDraftSingle(date || undefined)}
            className="rounded-md"
          />
        )}
      </div>

      <div className="flex flex-col gap-2 border-t border-border bg-muted/20 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
        <p className="text-xs text-muted-foreground" data-testid="task-calendar-summary">
          {summary}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={handleClear} data-testid="task-calendar-clear">
            Clear
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={close} data-testid="task-calendar-cancel">
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={isRange ? !rangeComplete : false}
            onClick={handleConfirm}
            data-testid="task-calendar-confirm"
          >
            Confirm
          </Button>
        </div>
      </div>
    </>
  );

  // Filter range mode (no field trigger) uses a dialog so it stays viewport-safe.
  if (!trigger) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          hideClose
          priority
          className="w-[calc(100vw-1rem)] max-w-3xl overflow-hidden border-border bg-popover p-0 text-popover-foreground"
          data-testid={isRange ? 'task-date-range-calendar' : 'task-date-single-calendar'}
        >
          <DialogTitle className="sr-only">{isRange ? 'Select date range' : 'Select date'}</DialogTitle>
          <DialogDescription className="sr-only">
            Choose dates for the Tasks due-date filter.
          </DialogDescription>
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align={align}
        sideOffset={8}
        collisionPadding={12}
        className={cn(
          'z-[80] w-auto max-w-[min(760px,calc(100vw-1rem))] border-border bg-popover p-0 text-popover-foreground shadow-lg',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
        )}
        data-testid={isRange ? 'task-date-range-calendar' : 'task-date-single-calendar'}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {body}
      </PopoverContent>
    </Popover>
  );
}

export function TaskDateFieldButton({
  label,
  emptyLabel = 'Select date',
  className,
  ...props
}) {
  return (
    <Button
      type="button"
      variant="outline"
      className={cn('w-full justify-start gap-2 font-normal', className)}
      {...props}
    >
      <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="truncate">{label || emptyLabel}</span>
    </Button>
  );
}

export { toIsoDate, fromIsoDate, formatDisplayDate };
